import {
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  ExpandableSection,
  Grid,
  Header,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import {
  HEAT_PID_FIELDS_BY_SIDE,
  HEAT_ZONE_CONFIG_FIELDS_BY_SIDE,
  TUNING_SEED_PID_FIELDS_BY_SIDE,
  TUNING_TEMP_FIELDS_BY_SIDE,
} from 'features/plc/contracts/pressHMI.fields';
import {
  getPathName,
  PLC_PATHS,
  TUNING_MODE,
} from 'features/plc/contracts/pressHMI.schema';
import { usePLC, usePLCValue, usePLCValues } from 'features/plc/providers/PLCProvider';
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';

import HeatBlockStatus from '../components/heatBlockStatus';
import HeaterThermalStatus from '../components/heaterThermalStatus';
import HeatZoneConfig from '../components/heatZoneConfig';
import OverrideControls from '../components/overrideControls';
import PIDControls from '../components/pidControls';
import ProcessStatus from '../components/processStatus';
import SystemStatusAndControl from '../components/systemStatusAndControl';
import TemperaturePanel from '../components/temperaturePanel';
import usePidHistory from './hooks/usePidHistory';
import PidSnapshotDisplay from './pidSnapshotDisplay';
import TuningHeader from './tuningHeader';
import TuningHistory from './tuningHistory';

const TUNING_MODE_OPTIONS = [
  { label: 'Idle', value: TUNING_MODE.IDLE },
  { label: 'Tuning', value: TUNING_MODE.TUNING },
  { label: 'Heat', value: TUNING_MODE.HEAT },
  { label: 'Cooling', value: TUNING_MODE.COOLING },
];
const HEAT_SIDES = ['upper', 'lower'];
const PID_KEYS = ['kp', 'tnSeconds', 'tvSeconds', 'tdSeconds'];

const getHeatBlock = (side) => PLC_PATHS.status[`${side}HeatBlock`];
const getHeatPid = (side) => PLC_PATHS.cmd[`${side}HeatPid`];

const PID_STATUS_PATHS = HEAT_SIDES.flatMap((side) => {
  const heatBlock = getHeatBlock(side);

  return [
    ...PID_KEYS.map((key) => heatBlock.pid[key]),
    heatBlock.maxOverShoot,
  ];
});

const getPidSnapshotForSide = (statusValues, side) => {
  const heatBlock = getHeatBlock(side);

  return {
    ...Object.fromEntries(
      PID_KEYS.map((key) => [key, statusValues[getPathName(heatBlock.pid[key])]]),
    ),
    maxOvershoot: statusValues[getPathName(heatBlock.maxOverShoot)],
  };
};

const hasCompletePidSnapshot = (pidSnapshot) => HEAT_SIDES.every((side) => (
  PID_KEYS.every((key) => pidSnapshot?.[side]?.[key] > 0)
    && pidSnapshot?.[side]?.maxOvershoot > 0
));

function TuningContent() {
  const {
    activeWrite,
    pressId,
    runWrite,
    write,
    setWriteError,
  } = usePLC();
  const commandTuningMode = usePLCValue(PLC_PATHS.cmd.tuningRecipe.mode);
  const safetyOk = usePLCValue(PLC_PATHS.status.safetyOk);
  const statusValues = usePLCValues(PID_STATUS_PATHS);
  const {
    history,
    averages,
    completeSession,
    createSession,
  } = usePidHistory(pressId);
  const [savingCurrentValues, setSavingCurrentValues] = useState(false);
  const [savedCurrentPidSnapshotKey, setSavedCurrentPidSnapshotKey] = useState(null);
  const [activeTuningSession, setActiveTuningSession] = useState(null);

  const tuningModeActive = commandTuningMode === TUNING_MODE.TUNING;
  const currentPidSnapshot = useMemo(() => Object.fromEntries(
    HEAT_SIDES.map((side) => [side, getPidSnapshotForSide(statusValues, side)]),
  ), [statusValues]);
  const currentPidSnapshotKey = useMemo(
    () => JSON.stringify(currentPidSnapshot),
    [currentPidSnapshot],
  );
  const currentValuesSaved = savedCurrentPidSnapshotKey === currentPidSnapshotKey;
  const canSaveCurrentValues = Boolean(pressId)
    && tuningModeActive
    && hasCompletePidSnapshot(currentPidSnapshot)
    && !currentValuesSaved;

  const applyPidValuesForSide = useCallback(async (side, pidValues) => {
    try {
      await Promise.all(PID_KEYS.map((key) => write({
        handle: getPathName(getHeatPid(side)[key]),
        value: pidValues[key],
      })));
    } catch (error) {
      setWriteError(error.message);
      throw error;
    }
  }, [setWriteError, write]);

  const applyPidValues = useCallback(async (pidValues) => {
    await Promise.all(
      HEAT_SIDES.map((side) => applyPidValuesForSide(side, pidValues[side])),
    );
  }, [applyPidValuesForSide]);

  const saveAndApplyCurrentValues = useCallback(async () => {
    if (!canSaveCurrentValues) {
      return;
    }

    setSavingCurrentValues(true);
    try {
      await applyPidValues(currentPidSnapshot);
      let sessionToComplete = activeTuningSession;

      if (!sessionToComplete) {
        sessionToComplete = await createSession({
          isFullSession: true,
          upper: currentPidSnapshot.upper,
          lower: currentPidSnapshot.lower,
        });
      } else {
        await completeSession({
          session: sessionToComplete,
          upper: currentPidSnapshot.upper,
          lower: currentPidSnapshot.lower,
        });
      }

      setActiveTuningSession(null);
      setSavedCurrentPidSnapshotKey(currentPidSnapshotKey);
    } catch (error) {
      setWriteError(error.message);
    } finally {
      setSavingCurrentValues(false);
    }
  }, [
    applyPidValues,
    canSaveCurrentValues,
    completeSession,
    createSession,
    currentPidSnapshot,
    currentPidSnapshotKey,
    activeTuningSession,
    setWriteError,
  ]);

  const handleModeChange = useCallback(async (nextMode) => {
    if (nextMode === commandTuningMode) {
      return;
    }

    try {
      await runWrite({ path: PLC_PATHS.cmd.tuningRecipe.mode, value: nextMode });

      if (nextMode === TUNING_MODE.TUNING) {
        setSavedCurrentPidSnapshotKey(null);
        const nextSession = await createSession();
        setActiveTuningSession(nextSession);
        return;
      }

      setSavedCurrentPidSnapshotKey(null);

      if (commandTuningMode === TUNING_MODE.TUNING && activeTuningSession) {
        await completeSession({
          session: activeTuningSession,
          upper: currentPidSnapshot.upper,
          lower: currentPidSnapshot.lower,
          isAbandon: true,
        });
        setActiveTuningSession(null);
      }
    } catch (error) {
      setWriteError(error.message);
    }
  }, [
    activeTuningSession,
    commandTuningMode,
    currentPidSnapshot,
    createSession,
    completeSession,
    runWrite,
    setWriteError,
  ]);

  return (
    <ContentLayout header={<TuningHeader />}>
      <SpaceBetween size="xl">
        <Container
          header={(
            <Header
              variant="h2"
              actions={(
                <SpaceBetween direction="horizontal" size="xs">
                  {TUNING_MODE_OPTIONS.map((option) => (
                    <Button
                      key={option.value}
                      loading={activeWrite === getPathName(PLC_PATHS.cmd.tuningRecipe.mode)}
                      disabled={!safetyOk && option.value !== TUNING_MODE.IDLE}
                      variant={commandTuningMode === option.value ? 'primary' : 'normal'}
                      fullWidth
                      onClick={() => handleModeChange(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </SpaceBetween>
              )}
            >
              Tuning Controls
            </Header>
          )}
        >
          <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 6 }]}>
            <TemperaturePanel
              side="upper"
              fields={TUNING_TEMP_FIELDS_BY_SIDE.upper}
            />
            <TemperaturePanel
              side="lower"
              fields={TUNING_TEMP_FIELDS_BY_SIDE.lower}
            />
            <div>
              <ColumnLayout columns={2}>
                <ProcessStatus />
              </ColumnLayout>
            </div>
          </Grid>
        </Container>

        <Grid
          gridDefinition={[
            { colspan: 6 }, { colspan: 6 },
          ]}
        >
          <HeaterThermalStatus />
          <SystemStatusAndControl />
        </Grid>

        <ExpandableSection headerText="Seed PID Controls" variant="container" defaultExpanded headerDescription="These PID values are used as the starting point for auto-tuning mode.">
          <PIDControls
            fieldsBySide={TUNING_SEED_PID_FIELDS_BY_SIDE}
            upperTitle="Upper Seed PID"
            lowerTitle="Lower Seed PID"
          />
        </ExpandableSection>

        <ExpandableSection headerText="Heat PID Controls" variant="container" defaultExpanded headerDescription="These PID values are used to control the heating elements for Heat mode.">
          <PIDControls
            fieldsBySide={HEAT_PID_FIELDS_BY_SIDE}
            upperTitle="Upper Heat PID"
            lowerTitle="Lower Heat PID"
          />
        </ExpandableSection>

        <Container
          header={(
            <Header
              variant="h2"
              description="Review the live tuning result before saving it to history and applying it to the heat PID controls."
              actions={(
                <Button
                  variant="primary"
                  disabled={!canSaveCurrentValues}
                  loading={savingCurrentValues}
                  onClick={saveAndApplyCurrentValues}
                >
                  {currentValuesSaved ? 'Saved' : 'Save and Apply'}
                </Button>
              )}
            >
              Current Tuning Values
            </Header>
          )}
        >
          <SpaceBetween size="m">
            <StatusIndicator type={tuningModeActive ? 'in-progress' : 'stopped'}>
              {tuningModeActive ? 'Tuning in progress' : 'Tuning not active'}
            </StatusIndicator>
            <PidSnapshotDisplay entry={currentPidSnapshot} />
          </SpaceBetween>
        </Container>

        <TuningHistory
          history={history}
          pressId={pressId}
          averages={averages}
          onApply={applyPidValues}
          onApplySide={applyPidValuesForSide}
        />

        <ExpandableSection headerText="Override Controls" variant="container">
          <OverrideControls />
        </ExpandableSection>

        <ExpandableSection headerText="Upper Heat Zone Config" variant="container">
          <HeatZoneConfig fieldsBySide={HEAT_ZONE_CONFIG_FIELDS_BY_SIDE.upper} />
        </ExpandableSection>

        <ExpandableSection headerText="Lower Heat Zone Config" variant="container">
          <HeatZoneConfig fieldsBySide={HEAT_ZONE_CONFIG_FIELDS_BY_SIDE.lower} />
        </ExpandableSection>

        <ExpandableSection headerText="Heat Block Status" variant="container">
          <HeatBlockStatus />
        </ExpandableSection>
      </SpaceBetween>
    </ContentLayout>
  );
}

export default TuningContent;
